import React, { useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'react-styled-flexboxgrid';
import { StoryblokComponent, storyblokEditable } from 'gatsby-source-storyblok';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { bpWidthTailwind, colors, sizes } from '../../../styles/variables';
import { isBoolean, isFloat, isInt, mapColWidthProperties, mapSpaceCSS } from '../../../utils/helper';

/**
 * Height of the diagonal cut on an angled section.
 *
 * The cut is a `clip-path` on a decorative background layer rather than an opaque triangle painted
 * on top of the page. The original pseudo-element approach drew a `colors.beige100` triangle in an
 * absolutely positioned `::before`/`::after`, which painted above every non-positioned in-flow
 * element on the page — so when an angled section carried a negative top margin, that triangle
 * covered the text of the section above it (WEBDEV-1020 was the same overlay swallowing click
 * events). Clipping removes the background in the notch instead of covering what is behind it, so
 * the section above shows through, and it no longer assumes the block above is beige100.
 *
 * The clip lives on a `::before` layer rather than on `Background` itself because `clip-path`
 * clips an element's descendants too. Blocks inside a section carry their own Storyblok margins
 * and are routinely given negative ones so an image bleeds past the section edge and sits over the
 * diagonal — clipping the section cut those images off. Only the background layer is clipped here,
 * so content is never affected.
 */
const ANGLE_SIZE = '20vw';

/**
 * The background layer is inflated by this much above and below the section, and the diagonal is
 * pushed down the same amount so it still lands exactly on the section's corner.
 *
 * An element's background is snapped to whole device pixels, but a `clip-path` edge is antialiased
 * against the unsnapped geometry. On a section whose height is fractional — which any section
 * containing a `Media` block tends to be, since GatsbyImage sizes from the image's aspect ratio —
 * a clip landing exactly on the box edge renders as a half-transparent row, and the page shows
 * through it as a hairline gap. Bleeding the layer past the edge puts that soft row outside the
 * seam. This is the same allowance the original triangle made with `top: -1px` / `bottom: -1px`.
 */
const ANGLE_BLEED = '1px';

// Coordinates are relative to the inflated layer, so its top edge is `-ANGLE_BLEED` in section
// terms: a `y` of 0 sits one pixel above the section, and `ANGLE_SIZE + ANGLE_BLEED` sits exactly
// `ANGLE_SIZE` below its top. Each diagonal therefore runs corner to corner as before, with only
// its off-box end extended into the bleed.
const ANGLE_CLIP_PATHS = {
  topRight: `polygon(0 0, 100% calc(${ANGLE_SIZE} + ${ANGLE_BLEED}), 100% 100%, 0 100%)`,
  topLeft: `polygon(0 calc(${ANGLE_SIZE} + ${ANGLE_BLEED}), 100% 0, 100% 100%, 0 100%)`,
  bottomRight: `polygon(0 0, 100% 0, 100% calc(100% - ${ANGLE_SIZE} - ${ANGLE_BLEED}), 0 100%)`,
  bottomLeft: `polygon(0 0, 100% 0, 100% 100%, 0 calc(100% - ${ANGLE_SIZE} - ${ANGLE_BLEED}))`,
};

const isAngled = (angled) => Boolean(ANGLE_CLIP_PATHS[angled]);
const isTopAngled = (angled) => angled === 'topRight' || angled === 'topLeft';

/** The section's own background paint, applied either to the element or to its clipped layer. */
const backgroundPaint = css`
  background: ${({ withBg, customBg }) => (withBg ? customBg || colors.beige200 : undefined)};
  background: ${({ withBgImg, customBgImg }) => (withBgImg ? `url(${customBgImg})` || colors.beige200 : undefined)};

  ${({ withBgImg }) => {
    if (withBgImg) {
      return css`
        background-size: cover;
        background-repeat: no-repeat;
      `;
    }

    return undefined;
  }};
`;

const Background = styled.div`
  position: relative;

  /* Unangled sections paint their background directly on the element, as they always have. */
  ${({ angled }) => (isAngled(angled) ? undefined : backgroundPaint)};

  ${({ angled }) => {
    const clipPath = ANGLE_CLIP_PATHS[angled];

    if (!clipPath) {
      return undefined;
    }

    return css`
      &::before {
        content: '';
        position: absolute;
        /* Vertical bleed only — widening it would overflow the viewport on a full-width section. */
        inset: -${ANGLE_BLEED} 0;
        clip-path: ${clipPath};
        pointer-events: none;
        ${backgroundPaint};
      }
    `;
  }};
`;

const SectionCol = styled(Col)`
  &.custom-section-col {
    ${({ xs }) => {
      if (isInt(xs) || isFloat(xs)) {
        return css`
          flex-basis: ${(100 / sizes.gridCount.mobile) * xs}%;
          max-width: ${(100 / sizes.gridCount.mobile) * xs}%;
          display: block;
        `;
      }

      if (isBoolean(xs)) {
        if (xs) {
          return css`
            flex-grow: 1;
            flex-basis: 0;
            max-width: 100%;
            display: block;
          `;
        }

        return css`
          display: none;
        `;
      }

      return undefined;
    }};

    @media only screen and (${bpWidthTailwind.tablet}) {
      ${({ sm }) => {
        if (isInt(sm) || isFloat(sm)) {
          return css`
            flex-basis: ${(100 / sizes.gridCount.tablet) * sm}%;
            max-width: ${(100 / sizes.gridCount.tablet) * sm}%;
            display: block;
          `;
        }

        if (isBoolean(sm)) {
          if (sm) {
            return css`
              flex-grow: 1;
              flex-basis: 0;
              max-width: 100%;
              display: block;
            `;
          }

          return css`
            display: none;
          `;
        }

        return undefined;
      }};
    }

    @media only screen and (${bpWidthTailwind.desktopSm}) {
      ${({ md }) => {
        if (isInt(md) || isFloat(md)) {
          return css`
            flex-basis: ${(100 / sizes.gridCount.desktopSm) * md}%;
            max-width: ${(100 / sizes.gridCount.desktopSm) * md}%;
            display: block;
          `;
        }

        if (isBoolean(md)) {
          if (md) {
            return css`
              flex-grow: 1;
              flex-basis: 0;
              max-width: 100%;
              display: block;
            `;
          }

          return css`
            display: none;
          `;
        }

        return undefined;
      }};
    }

    @media only screen and (${bpWidthTailwind.desktopLg}) {
      ${({ lg }) => {
        if (isInt(lg) || isFloat(lg)) {
          return css`
            flex-basis: ${(100 / sizes.gridCount.desktopLg) * lg}%;
            max-width: ${(100 / sizes.gridCount.desktopLg) * lg}%;
            display: block;
          `;
        }

        if (isBoolean(lg)) {
          if (lg) {
            return css`
              flex-grow: 1;
              flex-basis: 0;
              max-width: 100%;
              display: block;
            `;
          }

          return css`
            display: none;
          `;
        }

        return undefined;
      }};
    }
  }
`;

const Section = ({ blok }) => {
  const { withBg, customBg = colors.beige200, withBgImg, customBgImg = '', anchorId, angled } = blok;
  const colWidth = useMemo(() => mapColWidthProperties(blok.config), [blok.config]);
  // A top-angled section is usually pulled up with a negative margin to reclaim the whitespace the
  // diagonal creates. That pull-up is authored in Storyblok as a fixed pixel value while the cut
  // itself is viewport-relative, so cap it at the height of the cut: beyond that the section stops
  // reclaiming empty space and starts overlapping the content of the section above.
  const spaceData = useMemo(
    () =>
      mapSpaceCSS(blok.config, blok._uid, {
        minMarginTop: isTopAngled(angled) ? `-${ANGLE_SIZE}` : undefined,
      }) || { marginCSS: '', paddingCSS: '', marginClass: '', paddingClass: '' },
    [blok.config, blok._uid, angled],
  );

  // Deliberately NOT the shared useViewport() context — that context's SSR/first-render default
  // is hardcoded to 1280 (desktop), so on a mobile client it would compute "visible" during
  // hydration when the section is actually supposed to be hidden (or vice versa), causing a real
  // mismatch. window.document.body.clientWidth (falling back to 0, i.e. mobile-first, during SSR)
  // is correct on the client's very first render, same convention as Col.jsx.
  const [viewWidth, setViewWidth] = useState(() =>
    typeof window !== 'undefined' ? window.document.body.clientWidth : 0,
  );

  useEffect(() => {
    const handleResize = () => setViewWidth(window.document.body.clientWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Pure function of colWidth + viewWidth (mirrors SectionCol's own mobile-first `min-width`
  // cascade below) so "is this section hidden at the current breakpoint" is known on the very
  // first render, server and client alike. The previous approach read the rendered display via
  // getComputedStyle in a ref callback, which only fires after first paint — every section that's
  // supposed to be hidden at the current breakpoint rendered visible for one frame first, then
  // snapped to hidden, a real layout shift (the dominant CLS contributor measured on /shop).
  const isHidden = useMemo(() => {
    const isDefinedColValue = (colVal) => isInt(colVal) || isFloat(colVal) || isBoolean(colVal);
    let activeColValue = colWidth.xs;
    if (viewWidth >= sizes.containerMinWidth.tablet && isDefinedColValue(colWidth.sm)) {
      activeColValue = colWidth.sm;
    }
    if (viewWidth >= sizes.containerMinWidth.desktopSm && isDefinedColValue(colWidth.md)) {
      activeColValue = colWidth.md;
    }
    if (viewWidth >= sizes.containerMinWidth.desktopLg && isDefinedColValue(colWidth.lg)) {
      activeColValue = colWidth.lg;
    }
    return activeColValue === false;
  }, [colWidth, viewWidth]);

  const marginClassName = useMemo(
    () => ['w-full box-border', spaceData.marginClass, isHidden ? 'hidden' : undefined].filter(Boolean).join(' '),
    [spaceData.marginClass, isHidden],
  );

  // `relative` on an angled section's content puts it in the same paint layer as the clipped
  // background layer above, where DOM order decides: the `::before` layer is generated first, so
  // content always paints over the diagonal rather than under it.
  const paddingClassName = useMemo(
    () =>
      [
        'w-full box-border',
        isAngled(angled) ? 'relative' : undefined,
        spaceData.paddingClass,
        isHidden ? 'hidden' : undefined,
      ]
        .filter(Boolean)
        .join(' '),
    [spaceData.paddingClass, isHidden, angled],
  );

  const dynamicCssBlock = `${spaceData.marginCSS}\n${spaceData.paddingCSS}`.trim();

  return (
    colWidth && (
      <>
        {dynamicCssBlock && <style dangerouslySetInnerHTML={{ __html: dynamicCssBlock }} />}

        <div {...storyblokEditable(blok)} className={`section-component--container ${marginClassName}`}>
          <Background
            withBg={withBg}
            customBg={customBg}
            withBgImg={withBgImg}
            customBgImg={customBgImg.filename}
            id={anchorId}
            angled={angled}
          >
            <div className={paddingClassName}>
              <Row>
                <SectionCol {...colWidth} className="custom-section-col">
                  {blok.container.map((nestedBlok) => (
                    <Row key={nestedBlok._uid}>
                      <StoryblokComponent blok={nestedBlok} />
                    </Row>
                  ))}
                </SectionCol>
              </Row>
            </div>
          </Background>
        </div>
      </>
    )
  );
};

Section.propTypes = {
  blok: PropTypes.object.isRequired,
};

export default Section;
